/**
 * Schema Validator Agent
 *
 * Validates JSON Schema to ensure field names don't contain the separator character
 * and that dependency constructs are well-formed.
 */

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { createIllegalCharPattern } from './constants';
import {
  SchemaValidationError,
  SchemaValidationResult,
  DependencyManagerConfig,
} from './types';

/**
 * Validates a JSON Schema against dependency manager requirements
 *
 * @param schema - The JSON Schema to validate
 * @param config - Dependency manager configuration
 * @returns Validation result with any errors found
 */
export function validateSchema(
  schema: JSONSchema7,
  config: DependencyManagerConfig,
): SchemaValidationResult {
  const errors: SchemaValidationError[] = [];
  const illegalCharPattern = createIllegalCharPattern(config.idSeparator);

  function validateFieldName(fieldName: string, path: string): void {
    if (illegalCharPattern.test(fieldName)) {
      errors.push({
        path,
        message: `Field name "${fieldName}" contains illegal character "${config.idSeparator}". This character is reserved for the field path separator.`,
        type: 'illegal_character',
      });
    }
  }

  function traverseSchema(
    currentSchema: JSONSchema7 | JSONSchema7Definition,
    currentPath: string = '',
  ): void {
    if (typeof currentSchema === 'boolean') {
      return;
    }

    // Check properties
    if (currentSchema.properties) {
      Object.keys(currentSchema.properties).forEach(key => {
        const fieldPath = currentPath ? `${currentPath}.${key}` : key;
        validateFieldName(key, fieldPath);

        const property = currentSchema.properties![key];
        if (typeof property !== 'boolean') {
          traverseSchema(property, fieldPath);
        }
      });
    }

    // Check patternProperties
    if (currentSchema.patternProperties) {
      Object.keys(currentSchema.patternProperties).forEach(pattern => {
        const property = currentSchema.patternProperties![pattern];
        if (typeof property !== 'boolean') {
          traverseSchema(
            property,
            currentPath ? `${currentPath}.<pattern>` : '<pattern>',
          );
        }
      });
    }

    // Check array items
    if (currentSchema.items) {
      if (Array.isArray(currentSchema.items)) {
        // Tuple validation
        currentSchema.items.forEach((item, index) => {
          if (typeof item !== 'boolean') {
            traverseSchema(
              item,
              currentPath ? `${currentPath}[${index}]` : `[${index}]`,
            );
          }
        });
      } else if (typeof currentSchema.items !== 'boolean') {
        // List validation
        traverseSchema(
          currentSchema.items,
          currentPath ? `${currentPath}[]` : '[]',
        );
      }
    }

    // Check oneOf
    if (currentSchema.oneOf) {
      currentSchema.oneOf.forEach((subSchema, index) => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(
            subSchema,
            currentPath ? `${currentPath}.oneOf[${index}]` : `oneOf[${index}]`,
          );
        }
      });
    }

    // Check anyOf
    if (currentSchema.anyOf) {
      currentSchema.anyOf.forEach((subSchema, index) => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(
            subSchema,
            currentPath ? `${currentPath}.anyOf[${index}]` : `anyOf[${index}]`,
          );
        }
      });
    }

    // Check allOf
    if (currentSchema.allOf) {
      currentSchema.allOf.forEach((subSchema, index) => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(
            subSchema,
            currentPath ? `${currentPath}.allOf[${index}]` : `allOf[${index}]`,
          );
        }
      });
    }

    // Check if/then/else
    if (currentSchema.if && typeof currentSchema.if !== 'boolean') {
      traverseSchema(
        currentSchema.if,
        currentPath ? `${currentPath}.if` : 'if',
      );
    }
    if (currentSchema.then && typeof currentSchema.then !== 'boolean') {
      traverseSchema(
        currentSchema.then,
        currentPath ? `${currentPath}.then` : 'then',
      );
    }
    if (currentSchema.else && typeof currentSchema.else !== 'boolean') {
      traverseSchema(
        currentSchema.else,
        currentPath ? `${currentPath}.else` : 'else',
      );
    }

    // Check dependencies (object form)
    if (currentSchema.dependencies) {
      Object.keys(currentSchema.dependencies).forEach(key => {
        validateFieldName(key, currentPath ? `${currentPath}.${key}` : key);
        const dep = currentSchema.dependencies![key];
        if (typeof dep === 'object' && !Array.isArray(dep)) {
          traverseSchema(
            dep as JSONSchema7,
            currentPath
              ? `${currentPath}.dependencies.${key}`
              : `dependencies.${key}`,
          );
        }
      });
    }
  }

  traverseSchema(schema);

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Throws an error if schema validation fails
 *
 * @param schema - The JSON Schema to validate
 * @param config - Dependency manager configuration
 * @throws Error if validation fails
 */
export function assertValidSchema(
  schema: JSONSchema7,
  config: DependencyManagerConfig,
): void {
  const result = validateSchema(schema, config);
  if (!result.valid) {
    const errorMessages = result.errors.map(
      err => `${err.path}: ${err.message}`,
    );
    throw new Error(`Schema validation failed:\n${errorMessages.join('\n')}`);
  }
}
